//get all suppliers

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from 'react-toastify';
import {
  createSupplier,
  updateSupplier,
  deleteSupplier,
  SupplierFormData,
  updateSupplierStatus,
  supplierStatus,
} from '../api/suppliers';
import { IEmployee } from '../api/employee';
import { getSuppliers, ISupplier, PaymentMethod } from '@/api/suppliers';

// DTO cho customer
export interface SupplierDTO {
  id: number;
  name: string;
  address: string;
  taxCode: string;
  phone: string;
  email: string;
  bankName: string;
  bankAccountNumber: string;
  bankAccountHolder: string;
  paymentMethod?: PaymentMethod | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  creator: IEmployee;
  status: supplierStatus;
}

// Chuyển đổi từ IOrderResponse sang OrderDTO
export const parseSupplierDTO = (supplier: ISupplier): SupplierDTO => ({
  id: supplier.id,
  name: supplier.name,
  address: supplier.address,
  taxCode: supplier.taxCode,
  phone: supplier.phone,
  email: supplier.email,
  bankName: supplier.bankName || '',
  bankAccountNumber: supplier.bankAccountNumber || '',
  bankAccountHolder: supplier.bankAccountHolder || '',
  paymentMethod: supplier.paymentMethod || null,
  createdAt: supplier.createdAt,
  updatedAt: supplier.updatedAt,
  deletedAt: supplier.deletedAt,
  creator: supplier.creator,
  status: supplier.status ? supplierStatus.ACTIVE : supplierStatus.INACTIVE,
});

// Lấy danh sách suppliers (tìm kiếm)
export const useSupplier = ({
  search,
  taxCode,
}: {
  search: string;
  taxCode: string;
}) => {
  return useQuery<{
    suppliers: SupplierDTO[];
  }>({
    queryKey: ['suppliers', search, taxCode],
    queryFn: async () => {
      const response = await getSuppliers(search, taxCode);
      const suppliers = response.map(parseSupplierDTO);
      return {
        suppliers,
      };
    },
    placeholderData: (previousData) => previousData,
  });
};
// Tạo supplier
export const useCreateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SupplierFormData) => {
      const response = await createSupplier(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Tạo nhà cung cấp thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Cập nhật supplier
export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: SupplierFormData;
    }) => {
      const response = await updateSupplier(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Cập nhật nhà cung cấp thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Xóa supplier
export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteSupplier(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Xóa nhà cung cấp thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Cập nhật trạng thái supplier
export const useUpdateSupplierStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: boolean }) => {
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['suppliers'] })
        .forEach((query) => {
          const data = query.state.data as {
            suppliers: SupplierDTO[];
          };
          if (data && Array.isArray(data.suppliers)) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              suppliers: data.suppliers.map((supplier: SupplierDTO) =>
                supplier.id === id
                  ? {
                      ...supplier,
                      status: status
                        ? supplierStatus.ACTIVE
                        : supplierStatus.INACTIVE,
                    }
                  : supplier
              ),
            });
          }
        });
      toast.success('Cập nhật trạng thái nhà cung cấp thành công');
      const response = await updateSupplierStatus(id, status);
      return response;
    },
    onError: (error, { id, status }) => {
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['suppliers'] })
        .forEach((query) => {
          const data = query.state.data as {
            suppliers: SupplierDTO[];
          };
          if (data && Array.isArray(data.suppliers)) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              suppliers: data.suppliers.map((supplier: SupplierDTO) =>
                supplier.id === Number(id)
                  ? {
                      ...supplier,
                      status: status
                        ? supplierStatus.ACTIVE
                        : supplierStatus.INACTIVE,
                    }
                  : supplier
              ),
            });
          }
        });
      toast.error(error.message);
    },
  });
};
